'use client';

import { useEffect } from 'react';
import { X, AlertCircle, CheckCircle2, Info } from 'lucide-react';

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: 'default' | 'danger';
};

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = '確認',
  cancelLabel = 'キャンセル',
  onConfirm,
  onCancel,
  variant = 'default',
}: ConfirmDialogProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200'>
      {/* Backdrop with blur */}
      <div
        className='absolute inset-0 bg-black/40 backdrop-blur-sm'
        onClick={onCancel}
      />
      
      {/* Dialog */}
      <div className='relative w-full max-w-md bg-white shadow-2xl animate-in zoom-in-95 duration-200'>
        {/* Header with accent bar */}
        <div
          className={`h-1 ${
            variant === 'danger' ? 'bg-rose-500' : 'bg-primary'
          }`}
        />
        
        <div className='p-8'>
          {/* Icon and Title */}
          <div className='mb-6 flex items-start gap-4'>
            <div
              className={`flex h-12 w-12 flex-shrink-0 items-center justify-center ${
                variant === 'danger'
                  ? 'bg-rose-50'
                  : 'bg-primary/10'
              }`}
            >
              {variant === 'danger' ? (
                <AlertCircle className='text-rose-600' size={24} />
              ) : (
                <Info className='text-primary' size={24} />
              )}
            </div>
            <div className='flex-1 pt-1'>
              <h3 className='mb-2 font-serif text-xl font-bold text-gray-900'>
                {title}
              </h3>
              <p className='text-[15px] leading-7 text-gray-600'>{message}</p>
            </div>
          </div>

          {/* Actions */}
          <div className='flex justify-end gap-3 border-t border-gray-100 pt-6'>
            <button
              onClick={onCancel}
              className='px-6 py-2.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50'
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              className={`px-6 py-2.5 text-sm font-bold text-white shadow-lg transition-all hover:shadow-xl ${
                variant === 'danger'
                  ? 'bg-rose-600 hover:bg-rose-700'
                  : 'bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70'
              }`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

type AlertDialogProps = {
  open: boolean;
  title: string;
  message: string;
  buttonLabel?: string;
  onClose: () => void;
  variant?: 'success' | 'error' | 'info';
};

export function AlertDialog({
  open,
  title,
  message,
  buttonLabel = '閉じる',
  onClose,
  variant = 'info',
}: AlertDialogProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  const variantStyles = {
    success: {
      accent: 'bg-emerald-500',
      iconBg: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
      button: 'bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600',
    },
    error: {
      accent: 'bg-rose-500',
      iconBg: 'bg-rose-50',
      iconColor: 'text-rose-600',
      button: 'bg-gradient-to-r from-rose-600 to-rose-500 hover:from-rose-700 hover:to-rose-600',
    },
    info: {
      accent: 'bg-primary',
      iconBg: 'bg-primary/10',
      iconColor: 'text-primary',
      button: 'bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200'>
      {/* Backdrop with blur */}
      <div
        className='absolute inset-0 bg-black/40 backdrop-blur-sm'
        onClick={onClose}
      />
      
      {/* Dialog */}
      <div className='relative w-full max-w-md bg-white shadow-2xl animate-in zoom-in-95 duration-200'>
        {/* Header with accent bar */}
        <div className={`h-1 ${styles.accent}`} />
        
        <div className='p-8'>
          {/* Icon and Title */}
          <div className='mb-6 flex items-start gap-4'>
            <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center ${styles.iconBg}`}>
              {variant === 'success' ? (
                <CheckCircle2 className={styles.iconColor} size={24} />
              ) : (
                <AlertCircle className={styles.iconColor} size={24} />
              )}
            </div>
            <div className='flex-1 pt-1'>
              <h3 className='mb-2 font-serif text-xl font-bold text-gray-900'>
                {title}
              </h3>
              <p className='whitespace-pre-line text-[15px] leading-7 text-gray-600'>{message}</p>
            </div>
          </div>

          {/* Action */}
          <div className='flex justify-end border-t border-gray-100 pt-6'>
            <button
              onClick={onClose}
              className={`px-8 py-2.5 text-sm font-bold text-white shadow-lg transition-all hover:shadow-xl ${styles.button}`}
            >
              {buttonLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
